<template>
    <div v-if="initial" :class="currentCountrySelectStatusClass">
        <switching-subscription-info
            v-if='showSwitchInfo'
            :current-subscription-title="currentSubscriptionTitle"
            :sim-info="simManager"
            :sub-id='currentSubscription?.id'
        />
        <sim-display
            v-else-if="isLocked"
            :title="$t('subscribe_plan.input_sim.device_title')"
            :card-id='uid'
        ></sim-display>
        <sim-display
            v-else
            :title="$t('subscribe_plan.input_sim.title')"
            :card-id='cardNum'
        ></sim-display>
        <div
            v-if='currentCountrySelectStatus===ECountrySelectStatus.SELECTED'
            class="plan-box"
        >
            <div class="box-title-container">
                <div class='box-title select-text'>
                    <div><span v-if="currentPlanDisplayMode === EPlanDisplayMode.SELECT && !showSwitchInfo">2. </span>{{ planBoxChooseTitle }}</div>

                </div>
                <p
                    class='help-me-choose'
                    @click="handleDialogClick">
                    {{ !isAddOn ? $t('subscribe.traffic.help_me_choose') : 'Rules of use' }} >
                </p>
            </div>
            <RichText class="country-promotion-text" v-if="countryPromotionText" :html="countryPromotionText"/>
            <!-- <p
                v-if="regionNote"
                class="region-notice"
                v-html="regionNote"
            ></p> -->
            <div
                v-if='legacyTag'
                class="legacyTips"
                v-html="$t('update_plan.legacy_tip')">
            </div>
            <template v-if="currentPlanDisplayMode === EPlanDisplayMode.VIEW">
                <combo-card
                    v-for="item of comboCardList"
                    v-bind="item"
                    :key="item.key"
                    :country='selectedCountry'
                    :subscribe-mode='subscribeMode'
                    :has-subscribed='hasSubscribed'
                    :default-selected-plan="defaultSelectedPlanId"
                    :curr-sub-plan-id="currSubPlanId"
                    :curr-sub-expired="subExpired"
                    :curr-device-groups='currDeviceGroup'
                    :promotions="promotions"
                    @change="handleSelectedPlanIdChange"
                    @toggleCombo="handleComboToggle"
                    ref="comboCards"
                ></combo-card>
            </template>
            <plan-cycle-card-group
                v-else
                :country="selectedCountry"
                :category="isAddOn ? ESeriesCategory.CLOUD_DATA_ADD_ON_PLAN : ESeriesCategory.CLOUD_DATA_PLAN"
                :country-available="isAddOn ? isAddOnCountryAvailable : isDataAvailableCountry"
                :default-selected-plan-id="defaultSelectedPlanId"
                :has-subscribed="hasSubscribed"
                :subscribe-mode="subscribeMode"
                :curr-sub-plan-id="currSubPlanId"
                :curr-sub-expired="subExpired"
                :curr-device-groups="currDeviceGroup"
                :promotions="promotions"
                :free-plan-submitting="freePlanSubmitting"
                @change="handleSelectedPlanIdChange"
                @duration-list-change="onPlanCycleDurationListChange"
                @visible-plans-change="onVisiblePlansChange"
                @subscribe="handlePlanCycleSubscribe"
                @claim-free-plan="handleFreePlanClaim"
            />
        </div>

        <template>
            <div v-if="currentPlanDisplayMode !== EPlanDisplayMode.SELECT" class="button-outer-container">
                <reo-common-button
                    v-if='subscribeMode===ESubscribeMode.SWITCH && !subExpired'
                    :custom-width="isMobile? '100%': buttonWidth"
                    :label="$t('update_plan.cancel_button')"
                    color-type="light"
                    @click="$router.go(-1)"
                ></reo-common-button>
                <reo-common-button
                    id="plans-select"
                    :custom-width="isMobile? '100%': buttonWidth"
                    :ga-label="gaLabel"
                    :label="buttonLabel"
                    :disabled="isButtonDisabled"
                    @click="handleFooterSubscribeClick"
                ></reo-common-button>
            </div>
            <transition name='fade'>
                <div class="bottom-button-container" v-if="bottomButtonShow && currentPlanDisplayMode !== EPlanDisplayMode.SELECT">
                    <reo-common-button
                        v-if='subscribeMode===ESubscribeMode.SWITCH && !subExpired'
                        :custom-width="isMobile? '100%': buttonWidth"
                        :label="$t('update_plan.cancel_button')"
                        color-type="light"
                        @click="$router.go(-1)"
                    ></reo-common-button>
                    <reo-common-button
                        :custom-width="isMobile? '100%': buttonWidth"
                        :ga-label="gaLabel"
                        :label="buttonLabel"
                        :disabled="isButtonDisabled"
                        @click="handleFooterSubscribeClick"
                    ></reo-common-button>
                </div>
            </transition>
        </template>

        <addon-desc-dialog
            v-if="showAddOnDescDialog"
            @cancel="toggleAddOnDesc"
        ></addon-desc-dialog>
        <help-plan-choose-dialog
            v-if="showPlanDescDialog"
            :isSim="true"
            :country='selectedCountry'
            :type="!isAddOn ? ESeriesCategory.CLOUD_DATA_PLAN : ESeriesCategory.CLOUD_DATA_ADD_ON_PLAN"
            @cancel="togglePlanDesc"
        ></help-plan-choose-dialog>
        <reo-dialog
            v-if="showOtherRegion"
            @cancel="showOtherRegion = false"
        >
            <div class="region-notice">
                {{ $t( 'subscribe.sim_not_available' ) }}
            </div>
        </reo-dialog>
        <email-verify-dialog
            v-model="showEmailVerify"
            @verified="handleButtonClick"
            :from="'sim-plan'"
        />
        <div
            v-if="freePlanSubmitting"
            class="sim-plan-free-claim-processing"
            aria-busy="true"
        >
            <reo-processing full-screen />
        </div>
    </div>
</template>

<script lang="ts">
import user from '@/api/user';
import reolinkGa from '@/common/js/gtagPageview';
import { unfoldError } from '@/common/js/notice';
import { isInVisibleArea } from '@/common/js/utils';
import {
    ECountrySelectStatus,
    EPlanDisplayMode,
    ESimCardModule,
    ESubscribeMode,
    ICloudService,
    IReoSelectOption,
    ESeriesCategory,
    IPromotionItem,
    IPlan
} from '@reolink-cloud/interface';
import { productsManager } from '@/lib';
import { uidHandler } from '@/lib/uidHandler';
import router from '@/router';
import { config } from 'CONFIG';
import nprogress from 'nprogress';
import Vue from 'vue';
import AddonDescDialog from '@/components/Dialogs/AddonDescDialog.vue';
import HelpPlanChooseDialog from '@/components/Dialogs/HelpPlanChooseDialog.vue';
import { generateCountryOptions, getIsSwitchAble, getLegacyTag } from '@/components/lib';
import { getComboCardList } from './lib';
import { createFreePlanOrder, goToFreePlanCompletePage, resolveFreePlanClaimError } from './lib/freePlanService';
import ReoCommonButton from '@/components/ReoCommonButton.vue';
import ComboCard, { IComboCardProps } from '@/components/SeriesCombo';
import { PlanCycleCardGroup } from '@/view/reolink/user/Subscribe/SIMPlan/components/plan-display';
import SwitchingSubscriptionInfo from '@/view/reolink/user/Subscribe/SIMPlan/components/SwitchingSubscriptionInfo.vue';
import SimDisplay from './SimDisplay.vue';
import { SimManager } from '@/lib/simManager';
import { getYmhFromQuery } from '@/common/js/query';
import { getSimCard } from '@/api/sim';
import { getIccidByUid } from '@/api/subscriptions';
import { fetchAllPages } from '@/utils/request';
import { getPromotionItems } from '@/api/order';

import { getCountryPromotionText, handleGetUserDataError, isValidIccid } from '@/utils/business';
import ReoDialog from '@/basic/Dialog.vue';
import { mapGetters } from 'vuex';
import EmailVerifyDialog from '@/components/EmailVerifyDialog.vue';
import ReoProcessing from '@/basic/processing.vue';
import { getPlanBlockList } from '@/api/plan';
import type { ISubscribeSimPageTopPayload } from '@/view/reolink/user/Subscribe/SIMPlan/SubscribeSim.vue';

export default Vue.extend({
    name: 'SIMSelectSection',
    components: {
        SwitchingSubscriptionInfo,
        ReoCommonButton,
        ComboCard,
        PlanCycleCardGroup,
        AddonDescDialog,
        HelpPlanChooseDialog,
        SimDisplay,
        ReoDialog,
        EmailVerifyDialog,
        ReoProcessing
    },
    inject: {
        setSubscribeSimPageTop: {
            from: 'setSubscribeSimPageTop',
            default(): ( payload: ISubscribeSimPageTopPayload | null ) => void {

                return () => { /* 非 SubscribeSim 父级时无操作 */ };

            }
        }
    },
    data () {

        const iccid = uidHandler.decrypt( this.$route.query.ymh as string );
        const isAddOn = this.$route.query.isAddOn ? true : false;
        const subscribeMode = this.$route.query.mode as string || ESubscribeMode.CREATE;
        return {

            config,
            ESeriesCategory,
            ESubscribeMode,
            ECountrySelectStatus,
            EPlanDisplayMode,

            initial: false,
            showCountrySelector: true,

            isAddOn,
            comboCardList: [] as IComboCardProps[],
            currentCountrySelectStatus: ECountrySelectStatus.SELECTED,
            currentPlanDisplayMode: iccid ? EPlanDisplayMode.SELECT : EPlanDisplayMode.VIEW,
            subscribeMode,
            showCurrentPlan: true,

            iccid,
            cardNum: iccid,
            uid:'',
            isLocked:false,
            productsManager,
            simManager: null as SimManager | null,

            locations: [] as IReoSelectOption[],

            currentSubscriptionTitle: '',
            currSubPlanId: '',
            defaultSelectedPlanId: '',
            planId: '',
            hasSubscribed: false,
            selectedCountry: 'US',
            legacyTag: null as string | null,

            showAddOnDescDialog: false,
            showPlanDescDialog: false,

            buttonWidth: 260,
            bottomButtonShow: false,

            promotions: [] as IPromotionItem[],
            showOtherRegion: false,
            countryPromotionText: undefined as string | undefined,
            showEmailVerify: false,
            visiblePlansForPromo: [] as IPlan[],
            freePlanSubmitting: false,

            /** 套餐卡片营销 RichText 触发的订阅，跳转购物车时可带 apply_promotion_usage_limit */
            pendingApplyPromotionUsageLimit: false,

            /** 仅由 PlanCycleCardGroup「duration-list-change」写入；用于 SELECT 标题，离开 SELECT（VIEW）时清空 */
            planCycleDurationListFromChild: [] as Array<{ unit: string; amount: number; }>,

        };

    },
    async beforeMount() {

        // 如果是从登录成功跳转回来的，则直接判断是否需要验证邮箱
        if ( this.$route.query.login ) {

            // 如果没有验证邮箱，则显示邮箱验证弹窗
            if ( !this.verified ) {

                this.showEmailVerify = true;
                return;

            }

            if ( this.currentCountrySelectStatus === ECountrySelectStatus.SELECTING ) {

                this.handleCountrySelectStatusChange();

            } else {

                await this.handleNextPage();

            }

        }

    },
    mounted() {

        window.addEventListener( 'scroll', this.calcPlanIsInWindow );

    },
    destroyed () {

        window.removeEventListener( 'scroll', this.calcPlanIsInWindow );

    },
    computed: {
        ...mapGetters( ['isDataAvailableCountry', 'isLogin'] as const ),

        currentCountrySelectStatusClass(): string {

            return this.currentCountrySelectStatus === ECountrySelectStatus.SELECTING ? 'country-selecting' : 'country-selected';

        },
        currentSubscription(): ICloudService | undefined {

            return this.simManager?.currentSubscriptionWithoutStatus;

        },

        subExpired(): boolean {

            return this.currentSubscription?.effectiveStatus === 'inactive';

        },

        isButtonDisabled(): boolean {

            // For add-on mode, check both data availability and add-on availability
            if ( this.isAddOn ) {

                return ( this.currentCountrySelectStatus !== ECountrySelectStatus.SELECTING &&
                    this.currentPlanDisplayMode === EPlanDisplayMode.SELECT &&
                    !this.planId ) || !this.isAddOnCountryAvailable;

            }

            return ( this.currentCountrySelectStatus !== ECountrySelectStatus.SELECTING &&
                this.currentPlanDisplayMode === EPlanDisplayMode.SELECT &&
                !this.planId ) || !this.isDataAvailableCountry;

        },

        buttonLabel(): string {

            if ( this.currentCountrySelectStatus === ECountrySelectStatus.SELECTING ) {

                return this.$t( 'common.next' ) as string;

            } else if ( this.isAddOn && !this.isAddOnCountryAvailable ) {

                return this.$t( 'subscribe.not_available' ) as string;

            } else if ( !this.isDataAvailableCountry ) {

                return this.$t( 'subscribe.not_available' ) as string;

            } else {

                if ( this.currentPlanDisplayMode === EPlanDisplayMode.VIEW ) {

                    if ( this.userStatus === 'logined' ) {

                        return this.$t( 'subscribe.login_subscribe_button' ) as string;

                    } else {

                        return this.$t( 'subscribe.not_login_subscribe_button' ) as string;

                    }

                } else {

                    return this.$t( 'cloud.noVerify.subscribe' ) as string;

                }

            }

        },

        currDeviceGroup(): string[] {

            return [];

        },

        gaLabel(): string {

            return this.currentPlanDisplayMode === EPlanDisplayMode.VIEW ? `Select SIM` : `Subscribe`;

        },

        userId(): string {

            return this.$store.getters.userId;

        },

        userCountry(): string {

            return this.$store.getters.country;

        },

        userStatus (): string {

            return this.$store.state.user.status;

        },

        isMobile(): boolean {

            return this.$store.getters.isMobile;

        },

        lang(): string {

            return this.$store.getters.lang;

        },

        selectedCountryText(): string | undefined {

            return this.locations.find( item => item.value === this.selectedCountry )?.label;

        },
        verified (): boolean {

            return this.$store.state.user.user.email.status === 'verified';

        },

        // Check if add-on (supplementary package) plans are available for selected country
        isAddOnCountryAvailable(): boolean {

            if ( !this.isAddOn ) return true;

            const planSeriesMap = productsManager.getPlanSeriesMap({
                category: ESeriesCategory.CLOUD_DATA_ADD_ON_PLAN,
                country: this.userCountry,
            });

            return Object.keys( planSeriesMap ).length > 0;

        },

        showSwitchInfo(): boolean {

            return this.subscribeMode === ESubscribeMode.SWITCH && !this.subExpired;

        },
        /** 仅在 SELECT 且子组件已 emit、且仅一种周期时显示 Select {cycle} Plan；否则默认文案 */
        planBoxChooseTitle(): string {

            if ( !this.initial ) {

                return this.$t( 'subscribe.planChooseTitle' ) as string;

            }
            if (
                this.currentPlanDisplayMode !== EPlanDisplayMode.SELECT
                || this.planCycleDurationListFromChild.length !== 1
            ) {

                return this.$t( 'subscribe.planChooseTitle' ) as string;

            }
            const d = this.planCycleDurationListFromChild[0];
            const cycle = this.$tc( 'cycle.' + d.unit, d.amount, { count: d.amount }) as string;
            return this.$t( 'subscribe.planChooseTitleSingleDuration', { period: cycle }) as string;

        }

    },
    methods: {

        async initProducts(): Promise<void> {

            const [,blockList] = await Promise.all( [
                productsManager.update({
                    lang: this.lang,
                    legacyTag: this.legacyTag ? this.legacyTag : undefined,
                }, this.subscribeMode === ESubscribeMode.CREATE || !this.legacyTag ),
                this.iccid ? getPlanBlockList( this.simManager?.data.esimDevice ? {
                    uid: this.uid,
                } : {
                    iccid: this.iccid,
                }) : Promise.resolve({ planIds: [] as string[] }),
            ] );
            productsManager.setBlockList( blockList.planIds );
            this.setDefaultSelectedCountry();
            const planType = this.isAddOn ? ESeriesCategory.CLOUD_DATA_ADD_ON_PLAN : ESeriesCategory.CLOUD_DATA_PLAN;
            let firstChargeData: Record<string, unknown>|undefined = {};
            if ( this.simManager?.data.esimDevice ) {

                firstChargeData['device'] = {
                    uid: this.simManager.data.esimDevice,
                    planCategories: [ planType ]
                };

            } else if ( this.iccid ) {

                firstChargeData['simCard'] = {
                    iccid: this.iccid,
                    planCategories: [ planType ]
                };

            } else {

                firstChargeData = undefined;

            }
            this.promotions = await fetchAllPages( getPromotionItems, { lang: this.lang, page: 1, limit: 100, country: this.selectedCountry,
                firstChargeData });

            if ( this.subscribeMode === ESubscribeMode.CREATE ) {

                if ( this.currentPlanDisplayMode === EPlanDisplayMode.SELECT ) {

                    /** 待补充已订购过免费套餐逻辑 */
                    this.hasSubscribed = false;

                    // this.hasSubscribed = await checkDeviceHadSubscription( this.uid );

                } else {

                    this.hasSubscribed = false;

                }

            } else {

                this.hasSubscribed = true;

            }

            this.locations = generateCountryOptions();

            this.comboCardList = getComboCardList( this.currentPlanDisplayMode, this.isAddOn );

        },

        async init() {

            if ( this.$route.name === 'sim-plan-choose' ) {

                await this.getSimManager();

                this.handleRouteQS();

                this.handleCountrySelectStatusChange();

            }
            if ( !this.isAddOn ) {

                this.subscribeMode = this.simManager?.currentSubscriptionWithoutStatus ? ESubscribeMode.SWITCH : ESubscribeMode.CREATE;

            }

            if ( this.subscribeMode === ESubscribeMode.SWITCH ) {

                await this.handleSwitchPlan();

            }

            await this.initProducts();

            this.initial = true;
            const HIDE_COUNTRY_KEY = 'hideDataCountryDialog';
            if ( !this.isDataAvailableCountry && !sessionStorage.getItem( HIDE_COUNTRY_KEY )) {

                this.showOtherRegion = true;
                sessionStorage.setItem( HIDE_COUNTRY_KEY, 'true' );

            }

            // Also show dialog for add-on mode when no plans available
            if ( this.isAddOn && !this.isAddOnCountryAvailable ) {

                this.showOtherRegion = true;

            }

            nprogress.done();

            this.syncSubscribeSimPageTop();

        },

        syncSubscribeSimPageTop(): void {

            const showSwitchHeader = this.subscribeMode === ESubscribeMode.SWITCH
                && !this.subExpired
                && !this.isAddOn;
            if ( showSwitchHeader ) {

                const title = this.$tc( 'dashboard.device_detail.more_menu.switch' );
                const subtitle = this.$tc( 'dashboard.device_detail.more_menu.switch_tip' );
                this.setSubscribeSimPageTop({ title, subtitle });
                return;

            }
            this.setSubscribeSimPageTop( null );

        },

        handleRouteQS(): void {

            if ( this.isAddOn && !this.currentSubscription?.id ) {

                unfoldError( 'No Valid Subscription', 3000 );

                window.setTimeout(() => {

                    router.push({
                        name: 'dashboard'
                    }).catch( console.error );

                }, 3000 );
                return;

            }

        },

        handleSelectedCountryChange( value: string ) {

            this.selectedCountry = value;
            this.calcPlanIsInWindow();

            let itemListName = `(Data-Plan)Country:${this.selectedCountry};`;
            let eventData = {
                item_list_name: itemListName,
                items: []
            };
            reolinkGa.sendECommerceEvent( 'view_item_list', eventData );
            let viewItemData = {
                currency: 'USD',
                value: 1,
                items: [
                    {
                        item_name: itemListName
                    }
                ]
            };
            reolinkGa.sendECommerceEvent( 'view_item', viewItemData );

        },

        async handleSwitchPlan(): Promise<void> {

            reolinkGa.sendEvent( 'switch', this.currentSubscription!.id );

            const planInfo = await productsManager.getPlanById( this.currentSubscription?.plan ?? '' );

            const seriesInfo = productsManager.getPlanSeriesByKey( planInfo.seriesKey );

            this.currentSubscriptionTitle = seriesInfo.i18n.name;

            if ( !getIsSwitchAble( this.currentSubscription )) {

                unfoldError( 'errors.frequent_switching' );
                window.setTimeout(() => {

                    router.push({
                        name: 'select-sim-card'
                    }).catch( console.error );

                }, 3000 );

            }

            this.legacyTag = await getLegacyTag( planInfo, seriesInfo.legacyTag );

            this.defaultSelectedPlanId = planInfo.portablePlan !== '0' ? planInfo.portablePlan : planInfo.id;

            this.currSubPlanId = planInfo.id;

        },

        handleSelectedPlanIdChange( planId: string ): void {

            this.planId = planId;

        },

        handlePlanCycleSubscribe( payload?: { fromPromotionRichText?: boolean; }): void {

            this.pendingApplyPromotionUsageLimit = Boolean( payload?.fromPromotionRichText );
            void this.handleButtonClick().catch( console.error );

        },

        /** 底部主按钮：不视为「从营销富文本跳转」，不带 apply_promotion_usage_limit */
        handleFooterSubscribeClick(): void {

            this.pendingApplyPromotionUsageLimit = false;
            this.handleButtonClick().catch( console.error );

        },

        async handleButtonClick() {

            if ( !this.isLogin ) {

                reolinkGa.sendSimpleEvent( 'sim list-Go to Select & Subscribe' );
                await user.goToAccountCenter( true, `${this.$route.fullPath}?login=true` );
                return;

            }

            if ( !this.verified ) {

                this.showEmailVerify = true;
                return;

            }

            if ( this.currentCountrySelectStatus === ECountrySelectStatus.SELECTING ) {

                this.handleCountrySelectStatusChange();

            } else {

                await this.handleNextPage();

            }

        },
        async handleFreePlanClaim( plan: IPlan ): Promise<void> {

            this.planId = plan.id;

            if ( !this.isLogin ) {

                reolinkGa.sendSimpleEvent( 'sim list-Go to Select & Subscribe' );
                await user.goToAccountCenter( true, `${this.$route.fullPath}?login=true` );
                return;

            }

            if ( !this.verified ) {

                this.showEmailVerify = true;
                return;

            }

            if ( this.freePlanSubmitting ) return;

            this.freePlanSubmitting = true;
            await this.$nextTick();

            try {

                const order = await createFreePlanOrder({
                    plan,
                    isAddOn: this.isAddOn,
                    lang: this.lang,
                    selectedCountry: this.selectedCountry,
                    iccid: this.simManager?.iccid,
                    parentSubscriptionId: this.simManager?.currentSubscription?.id
                });

                await goToFreePlanCompletePage( order.id );

            } catch ( error ) {

                console.error( error );
                const errorMessage = resolveFreePlanClaimError( error );
                unfoldError( errorMessage );

            } finally {

                this.freePlanSubmitting = false;

            }

        },
        async handleNextPage(): Promise<void> {

            if ( this.currentPlanDisplayMode === EPlanDisplayMode.VIEW ) {

                reolinkGa.sendSimpleEvent( 'sim list-Go to Select & Subscribe' );
                if ( this.userStatus === 'logined' ) {

                    await router.push({ name: 'select-sim-card' });

                    this.calcPlanIsInWindow();

                } else {

                    await user.goToAccountCenter( true, '/user/subscribe-plan/sim/select-sim/' );

                }

            } else {

                reolinkGa.sendSimpleEvent( 'sim list-Subscribe' );
                if ( this.planId === this.currSubPlanId ) {

                    this.subscribeMode = ESubscribeMode.RENEW;

                }
                const query: Record<string, string> = {
                    mode: this.subscribeMode,
                    ymh: uidHandler.encrypt( this.iccid ),
                    p_id: this.planId,
                    country: this.selectedCountry
                };

                if ( this.subscribeMode === ESubscribeMode.RENEW || this.subscribeMode === ESubscribeMode.SWITCH ) {

                    query.ps_id = this.currentSubscription!.id;

                }
                if ( this.pendingApplyPromotionUsageLimit ) {

                    query.apply_promotion_usage_limit = '1';

                }
                this.pendingApplyPromotionUsageLimit = false;
                await router.push({
                    name: 'cart',
                    query
                });

            }

        },

        setDefaultSelectedCountry(): void {

            let defaultAddressCountry: string = this.userCountry;
            const fallbackCountry = 'US';

            if ( this.isAddOn ) {

                const subscriptionCountry = this.currentSubscription!.country;
                this.selectedCountry = productsManager.getDefaultCountryKey( subscriptionCountry ) ?? fallbackCountry ;

            } else {

                const defaultCountryKey = productsManager.getDefaultCountryKey( defaultAddressCountry );
                this.selectedCountry = defaultCountryKey ?? fallbackCountry;

            }

            const defaultOption = this.locations.find(( location ) => {

                return location.value === this.selectedCountry;

            });

            if ( defaultOption ) {

                const newLocations = this.locations.filter(( location ) => {

                    return location.value !== defaultOption.value;

                });

                newLocations.unshift( defaultOption );

                this.locations = newLocations;

            }

        },

        handleCountrySelectStatusChange(): void {

            this.currentCountrySelectStatus = ECountrySelectStatus.SELECTED;
            this.showCountrySelector = false;
            this.calcPlanIsInWindow();

        },

        calcPlanIsInWindow(): void {

            window.setTimeout(() => {

                let visible = isInVisibleArea( '#plans-select' );
                this.bottomButtonShow = !visible && this.initial;

            }, 100 );

        },

        togglePlanDesc(): void {

            this.showPlanDescDialog = !this.showPlanDescDialog;

        },

        toggleAddOnDesc(): void {

            this.showAddOnDescDialog = !this.showAddOnDescDialog;

        },

        handleDialogClick(): void {

            reolinkGa.sendSimpleEvent( 'sim list-Help me choose' );
            if ( !this.isAddOn ) {

                this.togglePlanDesc();

            } else {

                this.toggleAddOnDesc();

            }

        },

        /** 显示/隐藏套餐描述时重新计算底部按钮位置 */
        handleComboToggle(): void {

            this.calcPlanIsInWindow();

        },

        onPlanCycleDurationListChange( list: Array<{ unit: string; amount: number; }> ): void {

            this.planCycleDurationListFromChild = list;

        },

        onVisiblePlansChange( plans: IPlan[] ): void {

            this.visiblePlansForPromo = plans;
            this.countryPromotionText = getCountryPromotionText( this.promotions, plans );

        },

        async getSimManager() {

            let iccid = getYmhFromQuery( this.$route );
            if ( !isValidIccid( iccid )) {

                const esimProfile = await getIccidByUid();
                iccid = esimProfile.find( item => item.uid === iccid )?.iccid ?? '';

            }
            this.iccid = iccid;

            const [rawSimData] = await Promise.all( [
                getSimCard({
                    iccid,
                    includes: [ESimCardModule.CLOUD_SERVICES, ESimCardModule.LAST_USED_DEVICE, ESimCardModule.SIM_CARD]
                }),
                productsManager.update({ lang: this.lang })
            ] );

            const simManager = new SimManager( rawSimData, rawSimData.services );
            await simManager.init({ trafficData: true });
            this.simManager = simManager;
            this.uid = rawSimData.esimDevice;
            this.isLocked = !!rawSimData.esimDevice;
            this.cardNum = rawSimData.simCard?.simCardCode ?? '';

        },
    },
    watch: {
        currentPlanDisplayMode( mode: EPlanDisplayMode ) {

            if ( mode === EPlanDisplayMode.VIEW ) {

                this.planCycleDurationListFromChild = [];

            }

        },
        userStatus: {

            async handler (): Promise<void> {

                try {

                    await this.init();

                } catch ( e ) {

                    handleGetUserDataError( e, this.$router );
                    nprogress.done();
                    this.setSubscribeSimPageTop( null );

                }

            },

            immediate: true,
        },
        initial( val: boolean ) {

            if ( val ) {

                this.$nextTick(() => {

                    if ( this.currentPlanDisplayMode === EPlanDisplayMode.VIEW ) {

                        const comboCards = this.$refs.comboCards as Array<InstanceType<typeof ComboCard>> ?? [];
                        this.countryPromotionText = getCountryPromotionText(
                            this.promotions,
                            comboCards.map( comboCard => ( Object.values( comboCard.planSeriesMapByStrategy ))
                                .filter( item => typeof item === 'object' )
                                .map( strategy => Object.values( strategy ))
                                .map( cycleMap => {

                                    return cycleMap.map( cycle => Object.values( cycle ).filter( item => typeof item === 'object' ).flat());

                                }))
                                .flat( 3 )
                        );

                    } else {

                        this.countryPromotionText = getCountryPromotionText( this.promotions, this.visiblePlansForPromo );

                    }

                });

            }

        },

    }
});
</script>

<style scoped lang="less">
    .country-selecting{
        background-color: @bg_02;
        border-radius: 12px;
        padding-bottom: 60px;
        .button-outer-container{
            margin-top: 32px;
            padding: 0 16px;
        }
        .country-selecting{
            padding-top: 60px;
            padding-bottom: 0;
        }
        .mobile({
            max-width: 528px;
        });
    }

    .select-text{
        font-family: @font_02;
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        font-weight: @sim-selection-title-font-weight;
        line-height: 1.2;
        @media @snapeak_pc{
            font-size: @sim-selection-title-font-size;
            color: @font_primary;
        }
        @media @snapeak_mobile{
            font-size: @sim-selection-title-font-size-mobile;
            flex-direction: column;
        }
    }

    .plan-box{
        background-color: @bg_02;
        border-radius: @border-radius-level-2;
        padding: 32px;
        margin-top: 24px;
        @media @snapeak_mobile{
            margin-top: 20px;
            padding: 16px;
        }
        .box-title-container{
            display: flex;
            justify-content: space-between;
            align-items: center;
            @media @mobile{
                align-items: flex-start;
                gap: 16px;
                flex-wrap: wrap;
            }
            .help-me-choose{
                .text-hover();
                font-size: 16px;
                font-weight: 500;
                flex-shrink: 0;
                .mobile({
                    font-size: 14px
                });
            }
        }
        .region-notice{
            font-size: 16px;
            font-weight: 500;
            line-height: normal;
            letter-spacing: 0em;
            width: 100%;
            color: @font_primary;
            strong{
                font-weight: 600;
            }

        }
        .country-promotion-text{
            font-size: 16px;
            color: #fa9550;
            margin-top: 8px;
            font-weight: 500;
        }
        .combo-card{
            padding-bottom: 24px;
            border-bottom: 1px solid @line;
            margin-bottom: 24px;
            &:last-of-type{
                border: none;
                margin-bottom: 24px;
                padding-bottom: 0;
            }
        }
    }

    .button-outer-container{
        gap: 20px;
        display: flex;
        justify-content: center;
        @media @snapeak_mobile{
            flex-direction: column;
            gap: 10px;
        }
        .button-outer-container();
    }

    .bottom-button-container{
        gap: 20px;
        display: flex;
        justify-content: center;
        @media @snapeak_mobile{
            flex-direction: column;
            gap: 10px;
        }
        .bottom-button-container();
    }

    .country-selected{
        .mobile({
            max-width: 528px;
            margin: 0 auto;
        });
    }

    .sim-plan-free-claim-processing {
        position: fixed;
        inset: 0;
        z-index: 100;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: rgba(0, 0, 0, 0.6);
        cursor: wait;
    }

</style>
